/* @flow */

import * as React from 'react';
import { useIntl } from 'react-intl';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import styles from './NotificationCard.style';

import messages from 'src/shared/app/notification/l10n/notificationL10n';

import UserAvatar, {
  small,
} from 'src/shared/components/domain/user/avatar/UserAvatar';

import CircledCheckIcon from 'src/shared/ui/component/icon/CircledCheckIcon';
import RecommendIcon from 'src/shared/ui/component/icon/RecommendIcon';
import InfoIcon from 'src/shared/ui/component/icon/InfoIcon';
import InvitationIcon from 'src/shared/ui/component/icon/InvitationIcon';
import ClockIcon from 'src/shared/ui/component/icon/ClockIcon';
import UserIcon from 'src/shared/ui/component/icon/UserIcon';
import MessageIcon from 'src/shared/ui/component/icon/MessageIcon';
import CancelIcon from 'src/shared/ui/component/icon/Cancelicon';
import CrossIcon from 'src/shared/ui/component/icon/CrossIcon';
import type { Notification } from '@braindate/domain/lib/notification/type';
import { Link } from 'react-router-dom';

import {
  getNotificationTemplate,
  getNotificationPath,
  getNotificationMessage,
  getNotificationSender,
  getNotificationDate,
  getNotificationIsRead,
  getNotificationId,
} from '@braindate/domain/lib/notification/util';
import DateTimeComp from 'src/shared/app/base/date/component/DateTime';

import trackingEvents from 'src/shared/core/service/tracking/trackingEvents';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import NotificationBadge from 'src/shared/ui/component/messaging/NotificationBadge';
import {
  useDismissNotificationMutation,
  useMarkNotificationAsReadMutation,
} from 'src/shared/app/base/api/endpoint/notificationsEndpoint';
import ReactTooltip from 'react-tooltip';

type Props = {
  notification: Notification,
  variant?: symbol,
};

export const templatreStringsMap = {
  braindate_message: 'braindate-message',
  group_braindate_message: 'group-braindate-message',
  braindate_invitation_received: 'braindate-invitation-received',
  braindate_invitation_changed: 'braindate-invitation-changed',
  braindate_invitation_declined: 'braindate-invitation-declined',
  braindate_confirmed: 'braindate-confirmed',
  group_braindate_full: 'group-braindate-full',
  group_braindate_join: 'group-braindate-join',
  fishbowl_braindate_join: 'fishbowl-braindate-join',
  group_braindate_leave: 'group-braindate-leave',
  fishbowl_braindate_leave: 'fishbowl-braindate-leave',
  braindate_cancelled: 'braindate-cancelled',
  group_braindate_cancelled: 'group-braindate-cancelled',
  fishbowl_braindate_cancelled: 'fishbowl-braindate-cancelled',
  braindate_early_reminder: 'braindate-early-reminder',
  group_braindate_early_reminder: 'group-braindate-early-reminder',
  fishbowl_braindate_early_reminder: 'fishbowl-braindate-early-reminder',
  topic_recommendation: 'topic-recommendation',
  user_recommendation: 'user-recommendation',
  braindate_tomorrow_agenda: 'braindate-tomorrow-agenda',
};

const mapTemplatesSettings = {
  [templatreStringsMap.braindate_message]: {
    title: messages.braindate_message,
    icon: <MessageIcon />,
  },
  [templatreStringsMap.group_braindate_message]: {
    title: messages.group_braindate_message,
    icon: <MessageIcon />,
  },
  [templatreStringsMap.braindate_invitation_received]: {
    title: messages.braindate_invitation_received,
    icon: <InvitationIcon />,
  },
  [templatreStringsMap.braindate_invitation_changed]: {
    title: messages.braindate_invitation_changed,
    icon: <ClockIcon />,
  },
  [templatreStringsMap.braindate_invitation_declined]: {
    title: messages.braindate_invitation_declined,
    icon: <CancelIcon />,
  },
  [templatreStringsMap.braindate_confirmed]: {
    title: messages.braindate_confirmed,
    icon: <CircledCheckIcon />,
  },
  [templatreStringsMap.group_braindate_full]: {
    title: messages.group_braindate_full,
    icon: <UserIcon />,
  },
  [templatreStringsMap.group_braindate_join]: {
    title: messages.group_braindate_join,
    icon: <UserIcon />,
  },
  [templatreStringsMap.fishbowl_braindate_join]: {
    title: messages.fishbowl_braindate_join,
    icon: <UserIcon />,
  },
  [templatreStringsMap.group_braindate_leave]: {
    title: messages.group_braindate_leave,
    icon: <UserIcon />,
  },
  [templatreStringsMap.fishbowl_braindate_leave]: {
    title: messages.fishbowl_braindate_leave,
    icon: <UserIcon />,
  },
  [templatreStringsMap.braindate_cancelled]: {
    title: messages.braindate_cancelled,
    icon: <CancelIcon />,
  },
  [templatreStringsMap.group_braindate_cancelled]: {
    title: messages.group_braindate_cancelled,
    icon: <CancelIcon />,
  },
  [templatreStringsMap.fishbowl_braindate_cancelled]: {
    title: messages.fishbowl_braindate_cancelled,
    icon: <CancelIcon />,
  },
  [templatreStringsMap.braindate_early_reminder]: {
    title: messages.braindate_early_reminder,
    icon: <InfoIcon />,
  },
  [templatreStringsMap.group_braindate_early_reminder]: {
    title: messages.group_braindate_early_reminder,
    icon: <InfoIcon />,
  },
  [templatreStringsMap.fishbowl_braindate_early_reminder]: {
    title: messages.fishbowl_braindate_early_reminder,
    icon: <InfoIcon />,
  },
  [templatreStringsMap.topic_recommendation]: {
    title: messages.topic_recommendation,
    icon: <RecommendIcon />,
  },
  [templatreStringsMap.user_recommendation]: {
    title: messages.user_recommendation,
    icon: <RecommendIcon />,
  },
  [templatreStringsMap.braindate_tomorrow_agenda]: {
    title: messages.braindate_tomorrow_agenda,
    icon: <ClockIcon />,
  },
};
const useStyles = createUseThemeStyles(styles);

const NotificationCard = ({ notification }: Props): React.Node => {
  const intl = useIntl();

  const { braindateSingular } = useEventBraindateName();

  const [markAsRead, { isLoading: isMarkingAsRead }] =
    useMarkNotificationAsReadMutation();
  const [dismiss, { isLoading: isDismissing }] =
    useDismissNotificationMutation();

  const isRead = getNotificationIsRead(notification);
  const sender = getNotificationSender(notification);
  const id = getNotificationId(notification);
  const elementId = `notification-card-${id}`;
  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
  */

  const classes = useStyles({
    unread: !isRead,
    isFetching: isDismissing || isMarkingAsRead,
  });

  /*
  |----------------------------------------------------------------------------
  | Events
  |----------------------------------------------------------------------------
  */

  React.useEffect(() => {
    ReactTooltip.rebuild();
  });

  const handleReadNotification = () => {
    trackingEvents.notification.click('Card');

    markAsRead(notification);
  };

  const handleOnClick = () => {
    trackingEvents.notification.click('Card');

    markAsRead(notification);
  };

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const avatarElt = sender && (
    <span className={classes.avatar}>
      <UserAvatar variant={small} alt="" user={sender} />
    </span>
  );

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    dismiss(notification);
  };

  const templateSetting =
    mapTemplatesSettings[getNotificationTemplate(notification)];

  const topElmIcon = templateSetting && (
    <span className={classes.notificationIcon}>
      {templateSetting && templateSetting.icon}
    </span>
  );
  const topElmTitle = templateSetting
    ? intl.formatMessage(
        templateSetting.title,
        braindateSingular ? { braindateSingular } : '',
      )
    : null;

  const topElt = (
    <div className={classes.top}>
      <div className={classes.type}>
        {topElmIcon} <span className={classes.firstLetter}>{topElmTitle}</span>
      </div>
    </div>
  );

  const notificationMessage = (
    <div className={classes.notificationMessage}>
      {getNotificationMessage(notification)}
    </div>
  );
  const dateTime = getNotificationDate(notification);

  const notificationDate = (
    <div className={classes.date}>
      <DateTimeComp date={dateTime} />
    </div>
  );

  const dismissElt = isRead && (
    <button
      onClick={handleDismiss}
      className={classes.dismiss}
      aria-label={intl.formatMessage(messages.dismiss)}
      aria-controls={elementId}
      disabled={isDismissing}
    >
      <CrossIcon />
    </button>
  );

  const markAsReadElt = !isRead && (
    <button
      data-tip={intl.formatMessage(messages.markAsRead)}
      data-place="left"
      className={classes.badgeButton}
      type="button"
      disabled={isMarkingAsRead}
      onClick={handleReadNotification}
    >
      <NotificationBadge />
    </button>
  );

  const path = getNotificationPath(notification);

  return (
    <div className={classes.root}>
      {topElt}

      <div className={classes.body} id={elementId}>
        <Link className={classes.link} to={path} onClick={handleOnClick}>
          {avatarElt}
          <div className={classes.text}>
            {notificationMessage}
            {notificationDate}
          </div>
        </Link>

        {dismissElt}
      </div>

      {markAsReadElt}
    </div>
  );
};

export default React.memo<Props>(NotificationCard);
